/**
 * Who did it, in words a hotel's staff can read: "you", a name, an
 * address — never a ZITADEL subject id.
 *
 * The api-server resolves a record's creator against the directory of
 * everyone who has signed in, at read time (`TaskSchedule.created_by_*`,
 * `MaintenanceWindow.created_by_*`, api-server 0.6.54). This picks what
 * to show from whatever came back.
 *
 * ORDER: the signed-in person themself as "you" (the one answer that
 * needs no directory at all), then the display name, then the email,
 * then "a team member". The name comes before the address for the
 * reason the Audit screen puts it there (`formatActor`): it is what
 * people call each other.
 *
 * NEVER THE RAW ID, which is where this parts company with `formatActor`.
 * The Audit screen is for supervisors tracing an action and shows an
 * unresolved subject because "something did this and we cannot name it"
 * is worth seeing there. Every role that can read a site sees who made
 * a schedule, the front desk included, and an 18-digit number is noise
 * to them — the rule the attention cards already keep ("Seen by a team
 * member", `state/attention/evidence.ts`). The calendar's "Booked by"
 * keeps it too since 2026-10-31 (`requesterLabel`): a dispatch's
 * requester is the signed-in person's address or name, but the api can
 * fall back to the bare sub.
 *
 * A shared front-desk iPad signs in as one account, so a schedule made
 * there names that account. That is the truth about who made it, and
 * this does not try to dress it up.
 */

/** A person as a record names them. `sub` may be absent or empty (an
 *  older api-server, a fixture); the label is then never "you". */
export interface PersonRef {
  readonly sub?: string | null;
  readonly email?: string | null;
  readonly displayName?: string | null;
}

export const SOMEONE_LABEL = 'a team member';

/**
 * A ZITADEL subject id as it would appear if a resolver ever fell back
 * to one: a long run of digits. No real name or address looks like
 * this, so one that does is treated as no name at all — the one
 * guarantee this module makes is worth a belt to go with the braces.
 */
const SUBJECT_ID_LIKE = /^\d{12,}$/;

function readable(value: string | null | undefined, sub: string): string | null {
  const text = value?.trim();
  if (!text || text === sub || SUBJECT_ID_LIKE.test(text)) return null;
  return text;
}

/**
 * `'you'` | the display name | the email | `'a team member'`.
 *
 * Lower-case "you" and "a team member" because the label sits inside a
 * sentence ("Created 12/Oct/2026 by you"); a caller starting a sentence
 * with it capitalises it there.
 */
export function personLabel(person: PersonRef, mySub: string | null | undefined): string {
  const sub = person.sub?.trim() ?? '';
  if (sub && mySub && sub === mySub) return 'you';
  return readable(person.displayName, sub) ?? readable(person.email, sub) ?? SOMEONE_LABEL;
}

/**
 * A task's requester as the record carries it -- one string, no
 * directory behind it (`booking.requester`, the calendar's "Booked by")
 * -- in the same words: an address or a name as written, else "a team
 * member". An email address still shows as it is.
 */
export function requesterLabel(who: string | null | undefined): string {
  return readable(who, '') ?? SOMEONE_LABEL;
}

/** The creator fields every record with a resolved creator carries. */
export interface CreatedByFields {
  readonly created_by_sub?: string | null;
  readonly created_by_email?: string | null;
  readonly created_by_display_name?: string | null;
}

/** Who made a schedule or a blocked time, as `personLabel` names them. */
export function creatorLabel(record: CreatedByFields, mySub: string | null | undefined): string {
  return personLabel(
    {
      sub: record.created_by_sub,
      email: record.created_by_email,
      displayName: record.created_by_display_name,
    },
    mySub,
  );
}

/** A label where it stands on its own (a "Set up by" value) rather than
 *  mid-sentence: "You", "A team member". A name or address is left as
 *  it was written. */
export function standaloneLabel(label: string): string {
  return label === 'you' || label === SOMEONE_LABEL
    ? label[0]!.toUpperCase() + label.slice(1)
    : label;
}
